import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import User from "../../models/User";
import {CarryOutOutlined, ClockCircleOutlined, ExclamationCircleFilled} from '@ant-design/icons';
import {useMutation} from "@tanstack/react-query";
import {Alert, Button, Input, Modal, Tree} from 'antd';
import {Link, useLocation, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import Form, {FormInputComponent} from '../../components/form/Form';
import TalentCandidate from '../../models/TalentCandidate';
import LoadingSpinner from '../../components/LoadingSpinner';
import SidebarComponent from "../../components/SidebarComponent";

import {createBreakpoint} from "react-use";

const useBreakpoint = createBreakpoint({ tablet: 768, mobile: 0 });

const ApplicationSideNavBar = ({ steps, currentStep, disabled, onStepNavigate, currentStepIndex, ...props }) => {
    const { auth , isUpdating} = useAuth();

    const breakpoint = useBreakpoint();
    const location = useLocation()
    const talentUser = props.user || auth;
    const isAdminEditing = auth?.canManageClientResourceTalent() && !!props.user;

    const talentSteps = useMemo(() => {

        if (!talentUser.isApplicationApproved()) {
            return steps
        }

        return TB.groupBy(steps, step => step.StepSection)
    }, [talentUser?.isApplicationApproved(), steps]);

    const sideBarTreeMenu = useMemo(() => {
        if (TB.isArray(talentSteps)) {
            return []
        }
        let menu = []
        TB.map(talentSteps, (sectionSteps, sectionKey) => {

            menu.push({
                title: sectionKey,
                key: `${TB.slugify(sectionKey)}`,
                icon: <CarryOutOutlined />,
                children: TB.map(sectionSteps, (step, index) => {
                    if (!step.StepTitle) return null;

                    let needsCompletion = step.NeedsCompletion(talentUser);

                    let isActive = currentStep.StepTitle === step.StepTitle;
                    let isCompleted =
                        (!needsCompletion && index <= currentStepIndex) ||
                        step.hasCompleted(talentUser || {});


                    return {
                        title: (
                            <div
                                className={TB.mapClass('flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}>
                                <i className={TB.mapClass("", {
                                    "fas fa-check-circle": needsCompletion && isCompleted,
                                    "fas fa-times-circle": needsCompletion && !isCompleted,
                                    "fas fa-arrow-alt-circle-right": !needsCompletion,
                                })} />

                                {step.StepTitle}
                            </div>
                        ),
                        key: `${TB.slugify(step.StepTitle)}`,
                    }
                })
            })
        })
        return menu
    }, [location?.pathname, talentSteps, talentUser, disabled]);
    if (!talentUser) return <div className="talent-app-nav lg:w-3/12 p-0" />


    return (

        <SidebarComponent canClose={breakpoint === 'mobile'} maxWidth="18rem" defaultClosed={false}
            openIcon={<i className="fas fa-caret-right" />}>
            <div className={TB.mapClass("talent-app-nav p-0 md:max-w-[280px]", {})}>

                <TalentProfileStatus talentUser={talentUser} isDisabled={disabled || isUpdating} />

                <h3 className="font-bold px-2">
                    {!talentUser.isApplicationApproved() ?
                        'Complete Your Application' :
                        'Edit Your Profile'
                    }
                </h3>


                {TB.isArray(talentSteps) ? (
                    talentSteps.map((step, index) => {
                        if (!step.StepTitle) return null;

                        const isActive = currentStep.StepTitle === step.StepTitle;
                        const isPrevious = index <= currentStepIndex;
                        const isNext = index > currentStepIndex;

                        const importActive = User.getResumeImportsForUser(talentUser?.id);
                        let needsCompletion = step.NeedsCompletion(talentUser);

                        const isCompleted = (!needsCompletion && isPrevious) ||
                            step.hasCompleted(talentUser || {});
                        const canGoToNext = isPrevious || (isCompleted && index === (currentStepIndex + 1));
                        return (
                            <button
                                type="button"
                                key={`step-nav-${index}`}
                                className={TB.mapClass('step-title pr-2', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isPrevious,
                                    isNext,
                                    canGoToNext,
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}
                                onClick={() => onStepNavigate(step)}
                            >
                                <i className={TB.mapClass("fas", {
                                    "fa-check-circle": needsCompletion && isCompleted,
                                    "fa-times-circle": needsCompletion && !isCompleted,
                                    "fa-arrow-alt-circle-right": !needsCompletion,
                                })} />

                                {step.StepTitle}
                            </button>
                        )

                    })
                ) : (
                    <React.Fragment>
                        <div className="pl-5">
                            <Tree
                                showLine={true}
                                showIcon={false}
                                defaultExpandedKeys={TB.map(sideBarTreeMenu, key => TB.slugify(key.key))}
                                onSelect={(selectedKeys, info) => {
                                    let key = TB.first(selectedKeys);
                                    if (!key) return;
                                    // console.log('onSelect', key, info)
                                    let step = TB.find(TB.flatMap(TB.values(talentSteps)), step => TB.slugify(step.StepTitle) === key);
                                    if (step) {
                                        // console.log('onstepnav', step)
                                        return onStepNavigate(step)
                                    }
                                }}
                                treeData={TB.map(talentSteps, (sectionSteps, sectionKey) => {
                                    return {
                                        title: sectionKey,
                                        key: `${TB.slugify(sectionKey)}`,
                                        icon: <CarryOutOutlined />,
                                        children: TB.map(sectionSteps, (step, index) => {
                                            if (!step.StepTitle) return null;

                                            let needsCompletion = step.NeedsCompletion(talentUser);

                                            let isActive = currentStep.StepTitle === step.StepTitle;
                                            let isCompleted =
                                                (!needsCompletion && index <= currentStepIndex) ||
                                                step.hasCompleted(talentUser || {});


                                            if (isActive) {
                                                // console.log(step.StepTitle, isCompleted, talentUser.validateWorkPreferences());
                                            }
                                            return {
                                                title: (
                                                    <div
                                                        className={TB.mapClass('profile-tree-link flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                                            isApproved: talentUser.isApplicationApproved(),
                                                            isCompleted,
                                                            isActive,
                                                            'no-click': disabled
                                                        })}>
                                                        <i className={TB.mapClass("", {
                                                            "fas fa-check-circle": needsCompletion && isCompleted,
                                                            "fas fa-times-circle": needsCompletion && !isCompleted,
                                                            "fas fa-arrow-alt-circle-right": !needsCompletion,
                                                        })} />

                                                        {step.StepTitle}
                                                    </div>
                                                ),
                                                key: `${TB.slugify(step.StepTitle)}`,
                                            }
                                        })
                                    }
                                })}
                            />
                        </div>
                    </React.Fragment>
                )}

            </div>
        </SidebarComponent>
    )
};

ApplicationSideNavBar.propTypes = {
    steps: PropTypes.any,
    currentStep: PropTypes.any,
    disabled: PropTypes.any,
    onStepNavigate: PropTypes.any,
};

export default ApplicationSideNavBar;

const TalentProfileStatus = ({ talentUser, isDisabled }) => {

    const [searchParams, setSearchParams] = useSearchParams();

    const currentProgress = useMemo(
        () => talentUser?.profileProgress(),
        [talentUser, isDisabled]
    );

    if (!talentUser.isApplicationApproved()) return null;

    // Resolve which state to render
    const effectiveState = (() => {
        if (talentUser.isProfileInProgress()) return 'in_progress';
        if (talentUser.isProfileReadyForApproval()) return 'ready_for_approval';
        if (talentUser.isPendingProfileApproved()) return 'under_review';
        if (talentUser.isProfileApproved()) return 'approved';
        if (talentUser.latest_appeal?.is_rejected) return 'appeal_rejected';
        if (talentUser.latest_appeal) return 'appeal_pending';
        if (talentUser.isProfileRejected()) return 'rejected';
        return null;
    })();

    if (!effectiveState) return null;

    const hasAppeal = effectiveState === 'appeal_pending' || effectiveState === 'appeal_rejected';

    const handleSubmit = async () => {
        if (!searchParams.has('submitForApproval') && !isDisabled) {
            let params = new URLSearchParams(searchParams);
            params.set('submitForApproval', '1');
            setSearchParams(params);
        }
    };

    // Per-state visual config
    const config = {
        in_progress: {
            card: { className: 'border border-slate-300 bg-white', style: {} },
            eyebrow: null,
            title: { text: 'Almost there!', className: 'text-gray-700' },
            desc: { text: 'Finish the remaining sections to submit your profile for approval.', className: 'text-gray-500' },
            primaryBtn: { label: 'Submit for Approval →', disabled: true, onClick: null },
            darkBg: false,
        },
        ready_for_approval: {
            card: {
                className: 'border-2 border-amber-400',
                style: {
                    background: 'linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.18)',
                },
            },
            eyebrow: {
                text: 'Action Required',
                className: 'text-amber-900',
                icon: (
                    <span className="relative ">
                        <ExclamationCircleFilled className="text-sm text-amber-400" />
                    </span>
                ),
            },
            title: { text: 'Submit for approval', className: 'text-slate-900' },
            desc: { text: "You're 100% complete, but clients can't see you yet. Wripple reviews in 1–2 business days.", className: 'text-amber-900' },
            primaryBtn: { label: 'Submit for Approval →', disabled: false, onClick: handleSubmit },
            darkBg: false,
        },
        under_review: {
            card: {
                className: 'border-2 border-purple-700',
                style: {
                    background: `linear-gradient(135deg, ${TB.config('color.secondary-darker')} 0%, ${TB.config('color.secondary')} 100%)`,
                    boxShadow: '0 4px 12px rgba(123, 92, 245, 0.25)',
                },
            },
            eyebrow: {
                text: talentUser.talent_profile_review_requested_at
                    ? `Submitted ${TB.moment(talentUser.talent_profile_review_requested_at).fromNow()}`
                    : 'Submitted',
                className: 'text-white/80',
                icon: <ClockCircleOutlined />,
            },
            title: { text: 'Under Review', className: 'text-white' },
            desc: { text: "We'll email you once Wripple has reviewed your profile.", className: 'text-white/90' },
            primaryBtn: null,
            darkBg: true,
        },
        approved: {
            card: {
                className: '',
                style: {
                    background: `linear-gradient(135deg, ${TB.config('color.success-darkest')} 0%, ${TB.config('color.success-darker')} 100%)`,
                    border: `1px solid ${TB.config('color.success-darker')}`,
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                },
            },
            eyebrow: {
                text: 'Live on Wripple',
                className: 'text-white/80',
                icon:             (
                    <span className="pulse-indicator pulse-relative mr-1">
                        <span className="blob success h-2 w-2"></span>
                    </span>
                ),
            },
            title: { text: "You're approved! 🎉", className: 'text-white' },
            desc: {
                text: `Clients can now find and book you.${talentUser.talent_profile_approved_at ? ` Approved ${TB.moment(talentUser.talent_profile_approved_at).format('MMM D, YYYY')}.` : ''}`,
                className: 'text-white/90',
            },
            primaryBtn: null,
            darkBg: true,
        },
        rejected: {
            card: { className: 'border-2 border-red-200 bg-red-50', style: {} },
            eyebrow: { text: 'Not Approved', className: 'text-red-600', icon: <i className="fas fa-times-circle" /> },
            title: { text: 'Profile not approved', className: 'text-red-900' },
            desc: { text: 'Your profile did not meet our current requirements. Submit an appeal for further review.', className: 'text-red-700' },
            primaryBtn: null,
            darkBg: false,
            showAppeal: true,
        },
        appeal_pending: {
            card: { className: 'border-2 border-orange-200 bg-orange-50', style: {} },
            eyebrow: { text: 'Appeal Submitted', className: 'text-orange-600', icon: <i className="fas fa-history" /> },
            title: { text: 'Appeal under review', className: 'text-orange-900' },
            desc: { text: 'A talent manager will review your appeal and reach out via email.', className: 'text-orange-700' },
            primaryBtn: null,
            darkBg: false,
        },
        appeal_rejected: {
            card: { className: 'border-2 border-red-200 bg-red-50', style: {} },
            eyebrow: { text: 'Appeal Rejected', className: 'text-red-600', icon: <i className="fas fa-times-circle" /> },
            title: { text: 'Appeal not approved', className: 'text-red-900' },
            desc: { text: 'Your appeal was not approved. You may submit a new appeal for further review.', className: 'text-red-700' },
            primaryBtn: null,
            darkBg: false,
            showAppeal: true,
        },
    }[effectiveState];

    const previewBtnStyle = config.darkBg
        ? { background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.35)', color: 'white' }
        : {borderColor: 'rgba(0,0,0,0.25)'};

    return (
        <>
            {/* Test state selector */}

            <div className={TB.mapClass("flex flex-col gap-2 items-stretch rounded-xl p-3 mx-3 mt-3", {
              [config.card.className]: true
            })} style={config.card.style}>

                {/* Progress bar (in_progress only) */}
                {effectiveState === 'in_progress' && (
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${currentProgress}%` }} />
                        </div>
                        <span className="text-[10px] font-bold text-gray-400">{currentProgress}%</span>
                    </div>
                )}

                {/* Eyebrow */}
                {config.eyebrow && (
                    <div className={`flex items-center gap-1.5 text-xxs font-bold uppercase tracking-wide mb-1 ${config.eyebrow.className}`}>
                        {config.eyebrow.icon}
                        {config.eyebrow.text}
                    </div>
                )}

                {/* Title */}
                <div className={`font-bold text-sm leading-tight mb-1 ${config.title.className}`}>
                    {config.title.text}
                </div>

                {/* Description */}
                <div className={`text-xs leading-snug mb-2.5 ${config.desc.className}`}>
                    {config.desc.text}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1.5">
                    {config.primaryBtn && (
                        <button
                            className="btn btn-block btn-primary"
                            disabled={config.primaryBtn.disabled || isDisabled}
                            onClick={config.primaryBtn.onClick}
                            style={config.primaryBtn.disabled ? {} : { background: '#7C3AED', borderColor: '#7C3AED', color: 'white' }}
                        >
                            {config.primaryBtn.label}
                        </button>
                    )}

                    {config.showAppeal && (
                        <ProfileAppealModal isAppeal={hasAppeal} talentUser={talentUser} />
                    )}
                    <Link to={`/talent-profiles/${talentUser.id}`} className="btn btn-block border" style={previewBtnStyle}>
                        Preview Client View
                    </Link>


                </div>
            </div>
        </>
    );
}


const ProfileAppealModal = ({ isAppeal, talentUser }) => {
    const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
    const [showAppealForm, setShowAppealForm] = useState(false);

    const { update: updateAuth, isUpdating } = useAuth();

    const handleClose = () => {
        setIsAppealModalOpen(false);
        setShowAppealForm(false);
    }

    const addTalentProfileAppealMutation = useMutation(['applicationProfileAppeal'],
        params => TalentCandidate.addTalentProfileAppeal(params),
        {
            onSuccess: async data => {
                handleClose();

                const updatedUser = User.make({ ...talentUser, latest_appeal: data.appeal });
                const didUpdate = await updateAuth(updatedUser);

                if (didUpdate) {
                    TB.alert('success', 'Profile Appeal Submitted', 'A talent manager will review your appeal and will reach out via email.')
                }
            },
            onError: err => {
                console.log(err, 'Error addTalentProfileAppealMutation');
            }
        }
    )

    return (
        <div className="flex flex-col items-stretch">
            <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={isUpdating}
                onClick={() => setIsAppealModalOpen(true)}
            >
                View Feedback
            </button>

            <Modal
                title={`${isAppeal ? 'Appeal' : 'Profile'} Not Approved`}
                closable={true}
                maskClosable={true}
                style={{ top: 150 }}
                destroyOnClose={true}
                width={500}
                footer={null}
                open={isAppealModalOpen}
                onCancel={handleClose}
                onClose={handleClose}
            >

                {showAppealForm ? (
                    <Form
                        showRequired
                        background={'transparent'}
                        title={null}
                        subtitle={null}
                        submitButton={false}
                        className="[&>.form-container]:p-0"
                        style={{ padding: 20 }}
                        onSubmit={async fields => addTalentProfileAppealMutation.mutateAsync(fields)}
                        isDisabled={addTalentProfileAppealMutation.isLoading}
                        fields={[]}
                    >
                        <div>
                            <label htmlFor="appeal-reason" className="block pb-1">
                                Write a note to explain your reason for your profile appeal. After your appeal is
                                submitted, a Wripple Talent Manager will review and reach out via email if there are any
                                questions.
                            </label>

                            <FormInputComponent
                                fieldName="notes"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                required
                                label="Notes"
                                type="antd-input"
                            >
                                <Input.TextArea
                                    showCount
                                    bordered
                                    id="appeal-reason"
                                    autoSize={{ minRows: 4 }}
                                    maxLength={500}
                                />
                            </FormInputComponent>
                        </div>
                        <div className="flex flex-row justify-between items-center gap-3">
                            <div className="mr-auto">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setShowAppealForm(false)}
                                >
                                    Back
                                </button>

                                {addTalentProfileAppealMutation.failureReason && (
                                    <p className="text-danger mr-auto">{addTalentProfileAppealMutation.failureReason?.message}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={addTalentProfileAppealMutation.isLoading}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <div>
                            <p>
                                Your {isAppeal ? 'appeal' : 'profile'} is not approved at this time.
                                Further feedback from the Wripple Talent Team has been provided below.
                                {!isAppeal ? ' If you believe this decision is incorrect, you may request an appeal at any time using the button below.' : ''}
                            </p>

                            <Alert
                                className="mt-4"
                                description={(
                                    <div>
                                        <p className="text-xs text-gray-500">
                                            Wripple Feedback:
                                        </p>

                                        <p className="pt-2">
                                            {talentUser.latest_appeal?.rejection_notes || talentUser.talent_profile_rejection_notes}
                                        </p>
                                    </div>
                                )}
                                type="warning"
                            />

                            {talentUser.latest_appeal && (
                                <div className="pt-4">
                                    <p>Your latest appeal pending review:</p>
                                    <Alert
                                        className="mt-2.5"
                                        description={(
                                            <div>
                                                <p className="text-xs text-gray-500">
                                                    Your Appeal:
                                                </p>

                                                <p className="pt-2">
                                                    {talentUser.latest_appeal.notes}
                                                </p>
                                            </div>
                                        )}
                                        type="info"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="pt-5 flex flex-row justify-between gap-3">
                            {/* {(talentUser.latest_appeal.is_approved || talentUser.latest_appeal.is_rejected) && ( */}
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => {
                                    if (talentUser.latest_appeal?.is_approved === false && talentUser.latest_appeal?.is_rejected === false) {
                                        handleClose();
                                        return TB.alert('warning', "Can't resubmit appeal at this time", 'Your current appeal is pending review.')
                                    }
                                    // if (talentUser.can_submit_appeal) {
                                    setShowAppealForm(true)
                                    // } else {
                                    //     handleClose();
                                    //     TB.alert('warning', "Can't resubmit appeal at this time", 'Your last appeal was not approved. You can resubmit an appeal 1 year from the date it was not approved if you still disagree with this decision.')
                                    // }
                                }}
                            >
                                Submit Appeal
                            </button>
                            {/* )} */}

                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    )
}